import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import PropertiesSidePanel, {
    SIDE_PANEL_ATTRIBUTE,
    SIDE_PANEL_GAP_PX,
    WINDOW_INSET_PX,
} from '~/components/Properties/PropertiesSidePanel';
import { RoiInterpretedType } from '~/domain/dicom/dicomstructureset';
import { RoiReadModel } from '~/domain/readmodels/StructureSetReadModel';
import StructureDetailsInformation from './StructureDetailsInformation';
import StructureStatistics from './StructureStatistics';
import StructureTypeMenu from './StructureTypeMenu';

/**
 * Structure Properties: the side panel beside the sidebar, its bottom one
 * gap above the window's, that lists a structure's properties and, for a
 * volume with contours, its statistics. Its edits run on top of it: the
 * Type menu opens beside the panel, level with the Type row, and the panel
 * stays open.
 */
export default function StructurePropertiesPanel({
    roi,
    anchorEl,
    volumeCm3,
    equivalentSphereDiameterCm,
    hasContourData,
    isEditable,
    onRename,
    onInterpretedTypeChange,
    onElectronDensityChange,
    onMassDensityChange,
    onClose,
}: {
    roi: RoiReadModel;
    /** An element in the sidebar; the panel opens right of the sidebar. */
    anchorEl: HTMLElement | null;
    volumeCm3: number;
    equivalentSphereDiameterCm: number;
    hasContourData: boolean;
    isEditable: boolean;
    onRename: (name: string) => void;
    onInterpretedTypeChange: (value: RoiInterpretedType) => void;
    onElectronDensityChange: (value: number | undefined) => void;
    onMassDensityChange: (value: number | undefined) => void;
    onClose: () => void;
}) {
    const [typeMenuAt, setTypeMenuAt] = useState<{
        left: number;
        top: number;
    }>();

    const openTypeMenu = (row: HTMLElement) => {
        const panel = row.closest(`[${SIDE_PANEL_ATTRIBUTE}]`) ?? row;
        setTypeMenuAt({
            left: panel.getBoundingClientRect().right + SIDE_PANEL_GAP_PX,
            top: row.getBoundingClientRect().top,
        });
    };

    return (
        <PropertiesSidePanel
            anchorEl={anchorEl}
            align="bottom"
            onClose={onClose}
            data-testid="structure-properties-panel"
            title={
                <FormattedMessage
                    id="datasetPicker.propertiesOf"
                    values={{ name: roi.name }}
                />
            }
        >
            <StructureDetailsInformation
                roi={roi}
                volumeCm3={volumeCm3}
                equivalentSphereDiameterCm={equivalentSphereDiameterCm}
                isEditable={isEditable}
                onRename={onRename}
                onEditType={openTypeMenu}
                onElectronDensityChange={onElectronDensityChange}
                onMassDensityChange={onMassDensityChange}
            />
            {!roi.isPointRoi && hasContourData && (
                <StructureStatistics roiNumber={roi.roiNumber} />
            )}
            <StructureTypeMenu
                roi={roi}
                open={typeMenuAt !== undefined}
                anchorReference="anchorPosition"
                anchorPosition={typeMenuAt}
                marginThreshold={WINDOW_INSET_PX}
                onChange={onInterpretedTypeChange}
                onClose={() => setTypeMenuAt(undefined)}
            />
        </PropertiesSidePanel>
    );
}
